import React, { useEffect, useState } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import "./GoogleMaps.css";
import { Img } from "../Img/index";
import GlobalDrawer from "../Drawer/index";
// import Input from "../Input/index";

export interface Location {
  lat: number;
  lng: number;
  address: string | null;
  state?: string | null;
  city?: string | null;
  postalCode?: string | null;
  place_id?: string | null;
  country?: string | null;
  label?: string;
  isError?: boolean;
}

interface GoogleMapsProps {
  onLocationClick: (location: Location) => void;
  selectedLocation: Location | null;
  initialLocation: Location | null;
  disable?: boolean;
  label?: boolean;
  isError?: boolean;
}

export const OldOnboardingGoogleMaps: React.FC<GoogleMapsProps> = ({
  onLocationClick,
  selectedLocation,
  initialLocation,
  disable,
  label,
  isError,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const [currentLocation, setCurrentLocation] = useState<Location | null>(
    initialLocation
  );
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchLocation, setSearchLocation] = useState("");
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!disable) {
      if (e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const formattedAddress = results[0].formatted_address;
            let place_id = results?.[0]?.place_id;
            let city = "";
            let postalCode = "";
            let country = "";
            let state = "";
            results?.[0]?.address_components.forEach((component) => {
              // if (component.types.includes('locality')) {
              //   city = component.long_name; // Direct city name
              // }
              if (component.types.includes("postal_code")) {
                postalCode = component.long_name; // Postal Code
              }
              if (component.types.includes("country")) {
                country = component.long_name; // Country Name
              }
              if (component.types.includes("administrative_area_level_3")) {
                city = component.long_name; // District-level city
              }
              if (component.types.includes("administrative_area_level_1")) {
                state = component.long_name;
              }
            });

            const clickedLocation: Location = {
              lat,
              lng,
              address: formattedAddress,
              state,
              country,
              postalCode,
              city,
              place_id,
            };
            onLocationClick(clickedLocation);

            setCurrentLocation(clickedLocation);

            if (marker) {
              marker.setPosition({ lat, lng });
            } else {
              const newMarker = new window.google.maps.Marker({
                position: { lat, lng },
                title: formattedAddress,
              });
              setMarker(newMarker);
            }
          } else {
            console.error("Geocoder failed due to: " + status);
          }
        });
      } else {
        console.error("e.latLng is null");
      }
    }
  };

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            setIsGettingLocation(false);
            if (status === "OK" && results && results[0]) {
              const formattedAddress = results[0].formatted_address;
              let place_id = results?.[0]?.place_id;
              let city = "";
              let postalCode = "";
              let country = "";
              let state = "";
              results?.[0]?.address_components.forEach((component) => {
                if (component.types.includes("postal_code")) {
                  postalCode = component.long_name;
                }
                if (component.types.includes("country")) {
                  country = component.long_name;
                }
                if (component.types.includes("administrative_area_level_3")) {
                  city = component.long_name;
                }
                if (component.types.includes("administrative_area_level_1")) {
                  state = component.long_name;
                }
              });

              const myLocation: Location = {
                lat: latitude,
                lng: longitude,
                address: formattedAddress,
                state,
                country,
                postalCode,
                city,
                place_id,
              };
              onLocationClick(myLocation);
              setCurrentLocation(myLocation);

              if (marker) {
                marker.setPosition({ lat: latitude, lng: longitude });
              } else {
                const newMarker = new window.google.maps.Marker({
                  position: { lat: latitude, lng: longitude },
                  title: formattedAddress,
                });
                setMarker(newMarker);
              }
            } else {
              const basicLocation: Location = {
                lat: latitude,
                lng: longitude,
                address: null,
              };
              onLocationClick(basicLocation);
              setCurrentLocation(basicLocation);

              if (marker) {
                marker.setPosition({ lat: latitude, lng: longitude });
              } else {
                const newMarker = new window.google.maps.Marker({
                  position: { lat: latitude, lng: longitude },
                  title: "My Location",
                });
                setMarker(newMarker);
              }
            }
          });
        },
        (error) => {
          setIsGettingLocation(false);
          console.error("Error getting current location:", error);
          alert("Unable to get your location. Please check your browser settings and try again.");
        }
      );
    } else {
      console.error("Geolocation is not supported in this browser.");
      alert("Geolocation is not supported in this browser.");
    }
  };

  useEffect(() => {
    if (selectedLocation) {
      if (marker) {
        const { lat, lng } = selectedLocation;
        marker.setPosition({ lat, lng });
      }
    }
  }, [selectedLocation, marker]);

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  const handleOpenDrawer = () => {
    setDrawerOpen(drawerOpen => true);
  };

  const handleCloseDrawer = () => {
    setSearchLocation(searchLocation => "");
    setDrawerOpen(drawerOpen => false);
  };

  const handleReset = () => {
    setSearchLocation(searchLocation => "");
    setCurrentLocation(currentLocation => "" as any);
    onLocationClick("" as any);

    if (marker) {
      marker.setMap(null); // Remove marker from map
      setMarker(null); // Clear marker state
    }

    handleCloseDrawer()
  };

  const handleSearch = () => {
    if (!searchLocation.trim()) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchLocation }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const place = results[0];
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        if (lat && lng) {
          let city = "";
          let postalCode = "";
          let country = "";
          let state = "";

          place.address_components.forEach((component) => {
            if (component.types.includes("postal_code")) {
              postalCode = component.long_name;
            }
            if (component.types.includes("country")) {
              country = component.long_name;
            }
            if (component.types.includes("administrative_area_level_3")) {
              city = component.long_name;
            }
            if (component.types.includes("administrative_area_level_1")) {
              state = component.long_name;
            }
          });

          const searchedLocation: Location = {
            lat,
            lng,
            address: place.formatted_address,
            state,
            country,
            postalCode,
            city,
          };

          onLocationClick(searchedLocation);
          setCurrentLocation(searchedLocation);

          if (marker) {
            marker.setPosition({ lat, lng });
          } else {
            const newMarker = new window.google.maps.Marker({
              position: { lat, lng },
              title: place.formatted_address,
            });
            setMarker(newMarker);
          }
        }
      } else {
        console.error("Geocoder failed due to:", status);
      }
    });
  };

  return (
    <div className="self-center">
      <button type="button" className={`flex gap-2 ${isError ? "text-red-700" : "text-blue-700"}`} onClick={handleOpenDrawer}>
        <Img src={"/images/new-landing-page/map.svg"} alt="image" className="min-w-10 min-h-10 max-w-10 max-h-10 object-contain rounded-full" />
        Locate Institute On Map
      </button>
      <GlobalDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        drawerHeading={label || `Locate Institute On Map`}
        width={650}
        placement="right"
      >
        <div className="googleMapContinerStyle">
          {!isLoaded ? (
            <h1>Loading...</h1>
          ) : (
            <>
              {!disable && (
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex items-center gap-2 w-full border border-solid rounded-lg py-[2.8px] px-2">
                    <input
                      disabled={disable}
                      className="rounded-lg w-full p-2 md:text-sm placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent"
                      placeholder={"Search ..."}
                      style={{
                        backgroundColor: "white",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "2.2vh",
                      }}
                      value={searchLocation}
                      onChange={(item) => {
                        setSearchLocation(searchLocation => item.target.value);
                      }}
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    type="button"
                    disabled={disable}
                    style={{ color: '#fff' }}
                    className="py-2 px-4 text-[2.2vh] font-medium text-[white] bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                  >
                    Search
                  </button>
                  {/* <button
                    onClick={handleUseMyLocation}
                    type="button"
                    disabled={disable || isGettingLocation}
                    style={{ color: '#fff' }}
                    className="py-2 px-4 text-[2.2vh] font-medium text-[white] bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 focus:outline-none disabled:opacity-50"
                  >
                    {isGettingLocation ? "Getting..." : "Use My Location"}
                  </button> */}
                </div>
              )}
              <div className="w-full my-2">
                <button
                  onClick={handleUseMyLocation}
                  type="button"
                  disabled={disable || isGettingLocation}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 text-[2.2vh] font-medium text-blue-700 bg-[white] border border-blue-700 rounded-lg hover:bg-blue-50 focus:ring-4 focus:ring-blue-300 focus:outline-none disabled:opacity-50"
                >
                  {isGettingLocation ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-blue-700" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                      Use My Location
                    </>
                  )}
                </button>
              </div>
              <GoogleMap
                mapContainerClassName="googleMapContainerStyleHandler"
                center={
                  selectedLocation
                    ? selectedLocation
                    : currentLocation
                      ? currentLocation
                      : { lat: 18.52043, lng: 73.856743 }
                }
                zoom={currentLocation ? 16 : 6}
                onClick={handleMapClick}
                options={{
                  disableDefaultUI: disable, // Hide default controls
                  draggable: !disable, // Disable dragging
                  zoomControl: !disable, // Disable zoom controls
                  scrollwheel: !disable, // Disable zooming with scroll
                  disableDoubleClickZoom: disable, // Disable zooming on double click
                  keyboardShortcuts: !disable, // Disable keyboard interactions
                  clickableIcons: !disable,
                }}
              >
                {selectedLocation ? (
                  <Marker
                    position={{
                      lat: selectedLocation.lat,
                      lng: selectedLocation.lng,
                    }}
                    title={selectedLocation.address || ""}
                    onLoad={(marker) => setMarker(marker)}
                  />
                ) : (
                  currentLocation && (
                    <Marker
                      position={{
                        lat: currentLocation.lat,
                        lng: currentLocation.lng,
                      }}
                      title={currentLocation.address || ""}
                      onLoad={(marker) => setMarker(marker)}
                    />
                  )
                )}
              </GoogleMap>
              {!disable && <div className="flex justify-end pt-4 space-x-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 text-[2.2vh] font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100"
                >
                  Reset
                </button>
                <button
                  disabled={disable}
                  type="button"
                  style={{ color: '#fff' }}
                  onClick={handleCloseDrawer}
                  className="inline-flex items-center px-4 py-2 text-[2.2vh] font-medium text-center text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 focus:outline-none"
                >
                  Save
                </button>
              </div>}
            </>
          )}
        </div>
      </GlobalDrawer>
    </div>
  );
};

export { OldOnboardingGoogleMaps as GoogleMaps };
